// Yoga Midsomer Norton - Town Location Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaMidsomerNorton = () => {
  usePageMeta({
    title: "Yoga Classes Midsomer Norton | Annelie Lily",
    description: "Yoga classes for Midsomer Norton, Somerset. Small group Hatha & Yin yoga in Frome, just 15 minutes away. Personal attention, beginners welcome.",
    canonical: "https://annelielily.com/areas/yoga-midsomer-norton"
  });

  const locationSchema = generateLocationSchema({
    townName: "Midsomer Norton",
    description: "Yoga classes for Midsomer Norton at Annelie Lily Yoga in Frome. Small group classes 15 minutes away.",
    url: "https://annelielily.com/yoga-midsomer-norton"
  });

  const faqs = [
    { question: "How far is Frome from Midsomer Norton?", answer: "About 15 minutes via the A362 through Radstock. An easy, scenic route." },
    { question: "What's the difference between Hatha and Yin yoga?", answer: "Hatha is more active — we move between poses, build strength. Yin is slower — we hold poses for minutes, targeting deep connective tissue. Both are valuable." },
    { question: "Do you offer classes for over 60s?", answer: "I teach students of all ages. Yoga can be adapted for any body — we work with what you have, not against it." },
    { question: "Is there parking available?", answer: "Yes, parking is available nearby. I'll send directions when you book." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Midsomer Norton" currentSlug="areas/yoga-midsomer-norton" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Midsomer Norton</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Midsomer Norton</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Midsomer Norton and its twin town <Link to="/areas/yoga-radstock" className="text-gold hover:underline">Radstock</Link> are 
              just 15 minutes from Frome. If you're looking for yoga that's more personal than a 
              gym class, you're in the right place.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />15 min from Midsomer Norton</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Midsomer Norton Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Midsomer Norton is well-connected to Frome via the A362 — just 15 minutes through 
              <Link to="/areas/yoga-radstock" className="text-gold hover:underline"> Radstock</Link>. If you've been 
              looking for yoga locally, my small classes offer something different from the 
              options in Bath.
            </p>
            <p>
              My <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> are 
              deliberately small (6-8 people) because I believe yoga works better when there's 
              space for individual attention. I offer <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha yoga</Link> for 
              active practice and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin yoga</Link> for deep release.
            </p>
            <p>
              If you're <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">new to yoga</Link>, 
              don't worry — many of my students started as complete beginners. I offer modifications 
              for every pose so you can work at your own level.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Midsomer Norton</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga", desc: "Traditional practice for strength, flexibility, and calm.", link: "/yoga-classes/hatha-yoga" },
              { title: "Yin Yoga", desc: "Slow, deep stretches for release and relaxation.", link: "/yoga-classes/yin-yoga" },
              { title: "Private Sessions", desc: "One-to-one yoga tailored to your needs.", link: "/yoga-classes/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions from Midsomer Norton Students</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Start?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Nearby areas:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Radstock", link: "/areas/yoga-radstock" },
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Wells", link: "/areas/yoga-wells" },
              { name: "Somerset", link: "/areas/yoga-somerset" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaMidsomerNorton;
